import * as React from "react";

import { cn } from "../../lib/utils";
import { AddonThumbnail } from "./AddonThumbnail";

export interface AddonRowProps {
  name: string;
  description?: string | null;
  /** Formatted unit price, e.g. "$15.00". */
  priceLabel: string;
  /** Billing unit, e.g. "per day" or "one time". */
  unitLabel: string;
  /** Trip total shown after the unit, e.g. "$30.00 for 2 nights". */
  tripLabel?: string | null;
  /** Already resolved photo URL. No photo means no photo box at all. */
  photoUrl: string | null;
  selected: boolean;
  /** Required add-ons are always selected and cannot be toggled. */
  required?: boolean;
  onToggle: () => void;
  onDetails: () => void;
  detailsExpanded?: boolean;
  detailsButtonRef?: React.Ref<HTMLButtonElement>;
  className?: string;
}

/**
 * One compact add-on row: optional photo, name, short description, price and
 * an Add / Added button. "View details" is a sibling button, never nested in
 * the toggle, so each keeps its own accessible name and keyboard behavior.
 */
export function AddonRow({
  name,
  description,
  priceLabel,
  unitLabel,
  tripLabel,
  photoUrl,
  selected,
  required = false,
  onToggle,
  onDetails,
  detailsExpanded = false,
  detailsButtonRef,
  className,
}: AddonRowProps) {
  const toggleName = required
    ? `${name} (required)`
    : `${selected ? "Remove" : "Add"} ${name}`;

  return (
    <div
      data-selected={selected}
      className={cn(
        "flex items-center gap-3 rounded-lg border p-3 transition-colors",
        selected
          ? "border-primary bg-primary/5"
          : "border-border bg-background hover:border-muted-foreground/40",
        className,
      )}
    >
      {photoUrl && (
        <AddonThumbnail src={photoUrl} name={name} className="h-14 w-14" />
      )}

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-foreground">{name}</span>
          {required && (
            <span className="rounded bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
              Required
            </span>
          )}
        </div>
        {description && (
          <p className="mt-0.5 line-clamp-2 text-sm text-muted-foreground">
            {description}
          </p>
        )}
        <button
          ref={detailsButtonRef}
          type="button"
          onClick={onDetails}
          aria-label={`View details for ${name}`}
          aria-expanded={detailsExpanded}
          className="mt-1 text-sm text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          View details
        </button>
      </div>

      <div className="flex shrink-0 items-center gap-3">
        <div className="text-right">
          <div className="font-semibold text-foreground">{priceLabel}</div>
          <div className="text-xs text-muted-foreground">
            {tripLabel ? `${unitLabel} · ${tripLabel}` : unitLabel}
          </div>
        </div>
        <button
          type="button"
          onClick={onToggle}
          disabled={required}
          aria-pressed={selected}
          aria-label={toggleName}
          className={cn(
            "min-w-[4.5rem] rounded-md border px-3 py-1.5 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed",
            selected
              ? "border-primary bg-primary text-primary-foreground"
              : "border-primary bg-background text-primary hover:bg-primary/5",
            required && "opacity-70",
          )}
        >
          {selected ? "Added" : "Add"}
        </button>
      </div>
    </div>
  );
}
